import {
  Center,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  useMediaQuery,
} from '@chakra-ui/react';
import type { KeyboardEvent } from 'react';
import { ANALYTICS_EVENTS } from '../../../../constants/analytics';
import { safeAnalytics } from '../../../../utils/analytics';
import { useIsMobileViewport } from '../../../../hooks/ui/useIsMobileViewport';
import { BowerSpinner } from '../../../ui/BowerSpinner';
import { CreateDestinationChip } from './CreateDestinationChip';
import { CreateModalHeader } from './CreateModalHeader';
import { CreateModalView } from './CreateModalView';
import { viewTitle } from './createModalCopy';
import { useCreateChoices } from './useCreateChoices';
import {
  useCreateModalController,
  type CreateModalView as CreateModalViewState,
} from './useCreateModalController';
import { useCreateShortcuts } from './useCreateShortcuts';

/**
 * The one Create modal (docs/planning/global-create-modal.md), mounted once by
 * Layout and opened from every create entry point through `useOpenCreate`.
 *
 * Full screen below `md`; on desktop a fixed-size frame that does not jump
 * between steps (the body scrolls). Esc backs out of the picker first, then
 * closes. The destination is on screen at every step. While a body has a
 * request in flight the modal holds: nothing closes, backs out or changes the
 * destination until it settles.
 */
export function CreateModal() {
  const ctrl = useCreateModalController();
  const { request, destination, view, isOpen, isLoading, busy } = ctrl;
  const isMobile = useIsMobileViewport();
  // The touch test useMobileDetection uses, without its device enumeration.
  const [isTouch] = useMediaQuery('(hover: none) and (pointer: coarse)');
  const showShortcuts = !isMobile && !isTouch;
  const choices = useCreateChoices(ctrl, ctrl.close);
  // While the default loads, a typed request already shows its own title.
  const pendingType = ctrl.pendingRequest?.type;
  const shownView: CreateModalViewState = pendingType
    ? { kind: 'details', type: pendingType }
    : view;

  const dismiss = () => {
    if (busy) return;
    safeAnalytics.track(ANALYTICS_EVENTS.CREATE_MODAL_DISMISSED, {
      surface: (request ?? ctrl.pendingRequest)?.surface ?? null,
      step: isLoading ? 'loading' : view.kind,
      type: shownView.kind === 'details' ? shownView.type : null,
    });
    ctrl.close();
  };
  const back = () => {
    if (busy) return;
    if (view.kind === 'picker' && view.creating) ctrl.setView({ ...view, creating: false });
    else if (view.kind === 'picker') ctrl.setView(view.returnTo);
    else if (view.kind === 'details' && !request?.type) ctrl.setView({ kind: 'choose' });
    else dismiss();
  };
  const onKeyDown = (event: KeyboardEvent) => {
    // A nested menu or input that handled Escape itself keeps it.
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    event.stopPropagation();
    if (view.kind === 'picker') back();
    else dismiss();
  };

  useCreateShortcuts(isOpen && showShortcuts && view.kind === 'choose' && !choices.isCommitting, [
    ...choices.actions.map((a) => ({ key: a.shortcut, run: () => void choices.choose(a) })),
    { key: 'M', run: () => ctrl.setView({ kind: 'details', type: 'phone' }) },
  ]);

  let step: 1 | 2 | undefined;
  if (isLoading) step = undefined;
  else if (view.kind === 'choose') step = 1;
  else if (view.kind === 'details') step = 2;
  const canGoBack = view.kind !== 'choose' && !busy;

  return (
    <Modal
      isOpen={isOpen || isLoading}
      onClose={dismiss}
      closeOnEsc={false}
      closeOnOverlayClick={!busy}
      size={{ base: 'full', md: 'xl' }}
      scrollBehavior="inside"
      isCentered
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        data-testid="create-modal"
        onKeyDown={onKeyDown}
        w={{ base: '100vw', md: 'min(640px, calc(100vw - 140px))' }}
        maxW={{ base: '100vw', md: 'min(640px, calc(100vw - 140px))' }}
        h={{ base: '100dvh', md: 'min(660px, calc(100vh - 64px))' }}
        my={{ base: 0, md: 'auto' }}
        pt={{ base: 'env(safe-area-inset-top)', md: 0 }}
        borderRadius={{ base: 0, md: 'xl' }}
        bg="bg.elevated"
      >
        <CreateModalHeader
          title={viewTitle(shownView)}
          step={step}
          onBack={canGoBack ? back : undefined}
          canClose={!busy}
        />
        <ModalBody px={{ base: 4, md: 6 }} pb={4} display="flex" flexDirection="column" minH={0}>
          {destination ? (
            <CreateModalView
              ctrl={ctrl}
              choices={choices}
              showShortcuts={showShortcuts}
              showPhone={!isMobile}
              onDone={ctrl.close}
            />
          ) : (
            <Center flex={1}>
              <BowerSpinner size="lg" />
            </Center>
          )}
        </ModalBody>
        {destination && view.kind === 'choose' && (
          <ModalFooter
            borderTop="1px solid"
            borderColor="border.subtle"
            justifyContent="space-between"
            gap={3}
            px={{ base: 4, md: 6 }}
            pb={{ base: 'calc(12px + env(safe-area-inset-bottom))', md: 3 }}
            pt={3}
          >
            <CreateDestinationChip
              destination={destination}
              fromView={ctrl.fromView}
              note={
                ctrl.viewOnlyName
                  ? `You can view ${ctrl.viewOnlyName} but not add to it`
                  : undefined
              }
              onChange={() => ctrl.setView({ kind: 'picker', returnTo: view })}
            />
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
}
